import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled/macro';
import useWindowSize from '@hooks/use-window-size';
import { createYoutubeEmbedUrl, createVimeoEmbedUrl } from '@utils/utils';
import { GTM_ID } from '../../../../common/constants/gtmIds';
import {
  YoutubeEmbedProps,
  SimplecastEmbedProps,
  VimeoEmbedProps,
} from './media-atoms.types';

const EmbedVideo: React.FC<VimeoEmbedProps | YoutubeEmbedProps> = ({
  src = '',
  className,
}) => {
  const ratio = 16 / 9;
  const windowSize = useWindowSize();
  const iframe = useRef<HTMLIFrameElement | null>(null);

  const [videoDimensions, setVideoDimensions] = useState({
    width: iframe.current ? iframe.current.clientWidth : null,
    height: iframe.current ? iframe.current.clientWidth / ratio : null,
  });

  useEffect(() => {
    let currentVideoWidth = iframe.current ? iframe.current.clientWidth : null;
    setVideoDimensions({
      width: currentVideoWidth,
      height: currentVideoWidth !== null ? currentVideoWidth / ratio : null,
    });
  }, [ratio, windowSize]);

  const title = src.includes('youtube') ? 'youtube-iframe' : 'vimeo-iframe';

  return (
    <iframe
      src={src}
      title={title}
      data-testid={title}
      width={videoDimensions.width !== null ? videoDimensions.width : undefined}
      height={
        videoDimensions.height !== null ? videoDimensions.height : undefined
      }
      style={{ border: 'none', overflow: 'hidden' }}
      seamless
      ref={iframe}
      className={className}
      allowFullScreen={true}
    ></iframe>
  );
};

const YoutubeEmbed: React.FC<YoutubeEmbedProps> = ({ src = '', className }) => {
  return <EmbedVideo src={createYoutubeEmbedUrl(src)} className={className} />;
};

const VimeoEmbed: React.FC<VimeoEmbedProps> = ({ src = '', className }) => {
  return <EmbedVideo src={createVimeoEmbedUrl(src)} className={className} />;
};

export const YouTubeEmbedAtom = styled(YoutubeEmbed)`
  border: 0;
  display: block;
  margin: 0 auto;
  width: 100%;
  border-radius: 8px;
`;

export const VimeoEmbedAtom = styled(VimeoEmbed)`
  border: 0;
  display: block;
  margin: 0 auto;
  width: 100%;
  border-radius: 8px;
`;

const SimplecastEmbed: React.FC<SimplecastEmbedProps> = ({
  width = '100%',
  height = '',
  src = '',
  className,
}) => {
  return (
    <iframe
      src={src}
      title="simplecast-iframe"
      data-testid="simplecast-iframe"
      height={height} /* WIP: height constrained to 200px on simplecast? */
      width={width}
      style={{ border: 'none', overflow: 'hidden' }}
      seamless
      className={className}
      data-gtm-id={GTM_ID.PLAY_PODCAST}
    ></iframe>
  );
};

export const SimplecastEmbedAtom = styled(SimplecastEmbed)`
  border: 0;
  display: block;
  margin: 0 auto;
  width: 100%;
`;
